import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LayoutGrid, Table2, Percent } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { API_BASE } from '@/utils/api';
import { money } from '@/components/tables/tableStates';
import { ProductPhoto } from '../staff/OrderTaker';

const ATTRS = [['sin_tacc', 'Sin TACC'], ['vegetariano', 'Vegetariano'], ['vegano', 'Vegano'], ['picante', 'Picante'], ['destacado', 'La más pedida']];

const AttrChips = ({ p, onToggle }) => (
  <div className="flex flex-wrap gap-1">
    {ATTRS.map(([k, l]) => (
      <button key={k} aria-pressed={!!p[k]} onClick={() => onToggle(k)} data-testid={`attr-${k}-${p.product_id}`}
        className={`h-7 px-2 rounded-full text-xs font-semibold border ${p[k] ? 'bg-[#2563EB] text-white border-[#2563EB]' : 'border-border text-muted-foreground'}`}>{l}</button>
    ))}
  </div>
);

export default function ModernMenuEditor() {
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [view, setView] = useState('cards');
  const [cat, setCat] = useState('all');
  const [sel, setSel] = useState([]);
  const [pct, setPct] = useState(10);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API_BASE}/menu`).then(r => setMenu(r.data));
  useEffect(() => { load(); }, []);
  const products = useMemo(() => menu.products.filter(p => cat === 'all' || p.category_id === cat), [menu, cat]);
  const save = async (p, body) => {
    setMenu(m => ({ ...m, products: m.products.map(x => x.product_id === p.product_id ? { ...x, ...body } : x) }));
    try { await axios.put(`${API_BASE}/products/${p.product_id}`, body); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); load(); }
  };
  const bulk = async () => {
    for (const id of sel) {
      const p = menu.products.find(x => x.product_id === id);
      await save(p, { price: Math.round(p.price * (1 + pct / 100)) });
    }
    toast.success(`Aumento de ${pct}% aplicado a ${sel.length} productos`); setSel([]);
  };
  const catName = (id) => menu.categories.find(c => c.category_id === id)?.name || '';
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="modern-menu-editor">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold mr-auto">Carta</h1>
        <div className="inline-flex rounded-lg bg-muted p-1" role="tablist">
          {[['cards', 'Tarjetas', LayoutGrid], ['table', 'Tabla y precios', Table2]].map(([k, l, I]) => (
            <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`menu-view-${k}`}
              className={`h-8 px-3 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 ${view === k ? 'bg-card shadow' : ''}`}><I className="w-4 h-4" />{l}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto">
        {[{ category_id: 'all', name: 'Todas' }, ...menu.categories].map(c => (
          <button key={c.category_id} onClick={() => setCat(c.category_id)} data-testid={`menu-cat-${c.category_id}`}
            className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold ${cat === c.category_id ? 'bg-[#111827] text-white' : 'bg-muted'}`}>{c.name}</button>
        ))}
      </div>
      {view === 'cards' ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {products.map(p => (
            <article key={p.product_id} className="m-card overflow-hidden" data-testid={`menu-card-${p.product_id}`}>
              <ProductPhoto url={p.image_url} alt={p.name} className="w-full h-36" />
              <div className="p-3 space-y-2">
                <div className="flex justify-between gap-2"><h3 className="font-semibold leading-tight">{p.name}</h3><span className="font-bold">{money(p.price)}</span></div>
                <p className="text-xs text-muted-foreground">{catName(p.category_id)}</p>
                <AttrChips p={p} onToggle={(k) => save(p, { [k]: !p[k] })} />
                <label className="flex items-center justify-between text-sm pt-1">Disponible<Switch checked={p.is_available} onCheckedChange={(v) => save(p, { is_available: v })} data-testid={`menu-available-${p.product_id}`} /></label>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <section className="m-card overflow-x-auto" data-testid="menu-table">
          <div className="flex items-center gap-2 p-3 border-b border-border">
            <Percent className="w-4 h-4" /><span className="text-sm font-semibold">Aumento masivo</span>
            <Input type="number" className="w-20 h-8" value={pct} onChange={e => setPct(Number(e.target.value))} data-testid="bulk-pct-input" />
            <Button size="sm" className="bg-[#2563EB] text-white" disabled={!sel.length} onClick={bulk} data-testid="bulk-apply-btn">Aplicar a {sel.length} seleccionados</Button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground">{['', 'Producto', 'Categoría', 'Precio', sel.length ? 'Nuevo precio' : '', 'Activo', 'Agotado'].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{products.map(p => {
              const on = sel.includes(p.product_id);
              return (
                <tr key={p.product_id} className={`border-t border-border ${on ? 'bg-[#2563EB]/5' : ''}`}>
                  <td className="px-3"><Checkbox checked={on} onCheckedChange={() => setSel(on ? sel.filter(x => x !== p.product_id) : [...sel, p.product_id])} aria-label={`Seleccionar ${p.name}`} data-testid={`menu-select-${p.product_id}`} /></td>
                  <td className="px-3 py-2"><span className="flex items-center gap-2"><ProductPhoto url={p.image_url} alt="" className="w-10 h-10 rounded-md" />{p.name}</span></td>
                  <td className="px-3">{catName(p.category_id)}</td>
                  <td className="px-3">
                    {editing === p.product_id
                      ? <Input autoFocus type="number" defaultValue={p.price} className="h-8 w-28 border-2 border-[#2563EB]" data-testid={`menu-price-input-${p.product_id}`}
                          onBlur={e => { setEditing(null); if (Number(e.target.value) !== p.price) save(p, { price: Number(e.target.value) }); }} onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
                      : <button onClick={() => setEditing(p.product_id)} className={`tabular-nums font-semibold ${on ? 'line-through text-muted-foreground' : ''}`} data-testid={`menu-price-${p.product_id}`}>{money(p.price)}</button>}
                  </td>
                  <td className="px-3 font-bold m-accent-text tabular-nums">{on ? money(Math.round(p.price * (1 + pct / 100))) : ''}</td>
                  <td className="px-3"><Switch checked={p.is_available} onCheckedChange={(v) => save(p, { is_available: v })} data-testid={`menu-active-${p.product_id}`} /></td>
                  <td className="px-3"><Switch checked={!p.is_available} className="data-[state=checked]:bg-[#c2410c]" onCheckedChange={(v) => save(p, { is_available: !v })} data-testid={`menu-soldout-${p.product_id}`} /></td>
                </tr>
              );
            })}</tbody>
          </table>
        </section>
      )}
    </div>
  );
}
